import React, { ReactElement } from "react";
import { SearchRequestBaseModel, SortOrderType } from "../models/SearchRequestModel";
import { paginatorTemplate, OnPageChange, IPagedState, ConvertToISortModel } from "../Pages/Paginator";
import { Button } from "primereact/button";
import { ColumnFilterClearTemplateOptions, Column } from "primereact/column";
import { DataTable, DataTableSortMeta, DataTableStateEvent, DataTableFilterMeta } from "primereact/datatable";
import EmptyState from "./ui/EmptyState";
import ColumnSelector, { ColumnOption } from "./ui/ColumnSelector";

/**
 * Header can be:
 * - a plain ReactNode
 * - a function receiving (hasActiveFilters)
 * - a function receiving (hasActiveFilters, columnSelector) when enableColumnSelector is true
 */
type HeaderTemplate =
    | React.ReactNode
    | ((hasActiveFilters: boolean, columnSelector?: React.ReactNode) => React.ReactNode);

interface ExtendedDataGridProps {
    header?: HeaderTemplate;
    value?: any[];
    loading: boolean;
    onChange: (searchRequest: SearchRequestBaseModel) => Promise<any>;
    onSorting?: (searchRequest: SearchRequestBaseModel) => Promise<any>;
    totalRecords: number;
    defaultSortField: string | null;
    defaultSortOrder: SortOrderType | null;
    children: any;
    onRowClick?: (event: any) => void;
    emptyMessage?: React.ReactNode;
    /** Column visibility selector. Enabled by default. Set to false to disable. */
    enableColumnSelector?: boolean;
}

interface ExtendedDataTableState extends IPagedState {
    visibleColumns: Set<string>;
}

/** Check if any filter in the DataTableFilterMeta has a non-empty value */
function hasFiltersApplied(filters: DataTableFilterMeta | null): boolean {
    if (!filters) return false;
    return Object.values(filters).some((meta: any) => {
        // PrimeReact stores filters as { value, matchMode } or { operator, constraints: [...] }
        if (meta?.constraints) {
            return meta.constraints.some((c: any) => c.value !== null && c.value !== undefined && c.value !== "");
        }
        return meta?.value !== null && meta?.value !== undefined && meta?.value !== "";
    });
}

/**
 * Parse Column children to extract column metadata for the selector.
 * Columns without a `field` prop (e.g. action columns) are always visible and excluded from the selector.
 *
 * Custom props on Column children:
 * - `defaultHidden?: boolean` — column is hidden by default, can be toggled on
 * - `alwaysVisible?: boolean` — column cannot be hidden (locked on)
 * - `columnLabel?: string` — override display name in the selector (defaults to `header`)
 */
function parseColumnChildren(children: ReactElement[]): {
    columnOptions: ColumnOption[];
    defaultVisible: Set<string>;
} {
    const columnOptions: ColumnOption[] = [];
    const defaultVisible = new Set<string>();

    children.forEach((element) => {
        const { field, header, defaultHidden, alwaysVisible, columnLabel } = element.props || {};

        // Skip columns without a field (action columns, etc.) — they're always shown
        if (!field) return;

        const label = columnLabel || (typeof header === "string" ? header : field);

        columnOptions.push({
            field,
            header: label,
            required: !!alwaysVisible,
        });

        if (!defaultHidden) {
            defaultVisible.add(field);
        }
    });

    return { columnOptions, defaultVisible };
}

export default class ExtendedDataTable extends React.Component<ExtendedDataGridProps, ExtendedDataTableState> {
    private dataTable: DataTable<any> | null = null;

    private get isColumnSelectorEnabled(): boolean {
        return this.props.enableColumnSelector !== false;
    }

    constructor(props: ExtendedDataGridProps) {
        super(props);

        const children = React.Children.toArray(props.children) as ReactElement[];
        const { defaultVisible } = parseColumnChildren(children);

        this.state = {
            multiSortMeta: this.props.defaultSortField
                ? [{ order: this.props.defaultSortOrder, field: this.props.defaultSortField } as DataTableSortMeta]
                : [],
            first: 0,
            page: 1,
            filters: null!,
            pageRowCount: 10,
            visibleColumns: defaultVisible,
        };
    }

    reset = () => {
        this.dataTable?.reset();
        this.setState({
            first: 0,
            filters: null!,
            multiSortMeta: this.props.defaultSortField
                ? [{ order: this.props.defaultSortOrder, field: this.props.defaultSortField } as DataTableSortMeta]
                : [],
            page: 1,
            pageRowCount: 10,
        });
        this.props.onChange({ filters: [], sortings: ConvertToISortModel(this.state.multiSortMeta), pageNumber: 1, pageSize: 10 });
    };

    componentDidMount(): void {
        this.props.onChange({ filters: [], sortings: ConvertToISortModel(this.state.multiSortMeta), pageNumber: 1, pageSize: 10 });
    }

    onFilterApply = (event: DataTableStateEvent) => {
        event.first = 0;
        OnPageChange(this, event, this.props.onChange);
    };
    filterClearTemplate = (options: ColumnFilterClearTemplateOptions) => {
        if (!options) {
            return <></>;
        }
        return <Button label="Clear" severity="secondary" outlined onClick={() => this.handleFilterClear(options)} />;
    };
    handleFilterClear = (options: ColumnFilterClearTemplateOptions) => {
        try {
            options.filterClearCallback();
        } catch (error) {
            console.log("Please Apply Filter First before clearing.");
        }
    };

    handleColumnVisibilityChange = (visibleColumns: Set<string>) => {
        this.setState({ visibleColumns });
    };

    renderColumnSelector = (): React.ReactNode => {
        const children = React.Children.toArray(this.props.children) as ReactElement[];
        const { columnOptions } = parseColumnChildren(children);
        if (columnOptions.length === 0) return null;

        return (
            <ColumnSelector
                columns={columnOptions}
                visibleColumns={this.state.visibleColumns}
                onChange={this.handleColumnVisibilityChange}
            />
        );
    };

    renderHeader = (): React.ReactNode => {
        const { header } = this.props;
        const columnSelectorNode = this.isColumnSelectorEnabled ? this.renderColumnSelector() : null;

        // Resolve the header content
        const headerContent = (() => {
            if (!header) return null;
            if (typeof header === "function") {
                return (header as (hasActiveFilters: boolean) => React.ReactNode)(hasFiltersApplied(this.state.filters));
            }
            return header;
        })();

        // If column selector is enabled, inject it into the toolbar if possible
        if (columnSelectorNode && headerContent && React.isValidElement(headerContent)) {
            // Clone the toolbar element and inject columnSelector via props
            return React.cloneElement(headerContent as React.ReactElement<{ columnSelector?: React.ReactNode }>, {
                columnSelector: columnSelectorNode,
            });
        }

        // Fallback: render standalone column selector when no header or non-element header
        if (columnSelectorNode) {
            return (
                <span className="flex justify-end items-center gap-3 px-4 py-3">
                    {columnSelectorNode}
                </span>
            );
        }

        return headerContent;
    };

    render(): React.ReactNode {
        const allChildren = React.Children.toArray(this.props.children) as ReactElement[];

        // Filter children based on column visibility when selector is enabled
        const visibleChildren = this.isColumnSelectorEnabled
            ? allChildren.filter((element) => {
                  const { field } = element.props || {};
                  // Columns without a field (action columns) are always visible
                  if (!field) return true;
                  return this.state.visibleColumns.has(field);
              })
            : allChildren;

        return (
            <DataTable
                ref={(reference) => (this.dataTable = reference)}
                header={this.renderHeader()}
                value={this.props.value}
                stripedRows={false}
                paginator={this.props.value && this.props.value.length > 0}
                paginatorTemplate={paginatorTemplate}
                first={this.state.first}
                rows={this.state.pageRowCount}
                sortMode="multiple"
                removableSort
                lazy
                filters={this.state.filters}
                totalRecords={this.props.totalRecords}
                onPage={(event) => OnPageChange(this, event, this.props.onChange)}
                onFilter={(event) => this.onFilterApply(event)}
                onSort={(event) => {
                    event.first = 0;
                    this.props.onSorting ? OnPageChange(this, event, this.props.onSorting) : OnPageChange(this, event, this.props.onChange);
                }}
                multiSortMeta={this.state.multiSortMeta}
                loading={this.props.loading}
                onRowClick={this.props.onRowClick}
                emptyMessage={this.props.emptyMessage ?? <EmptyState description="Try adjusting your filters or check back later." />}
            >
                {visibleChildren.map((element, i) => {
                    // Strip custom props before passing to PrimeReact Column
                    const { defaultHidden, alwaysVisible, columnLabel, ...columnProps } = element.props;
                    return (
                        <Column
                            {...columnProps}
                            key={i}
                            sortable={columnProps.sortable !== undefined ? columnProps.sortable : !!(columnProps.filterMatchModeOptions)}
                            filter={columnProps.filterMatchModeOptions && columnProps.filterMatchModeOptions != null}
                            showFilterOperator={false}
                            showAddButton={false}
                            filterClear={this.filterClearTemplate}
                        />
                    );
                })}
            </DataTable>
        );
    }
}
